import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import { t, text } from '../helpers';
const brands = ['bmw', 'mercedesbenz', 'volkswagen', 'audi', 'peugeot', 'citroen', 'renault', 'opel', 'ford', 'toyota', 'skoda', 'volvo', 'hyundai', 'kia', 'nissan', 'honda', 'mazda', 'mitsubishi', 'suzuki', 'subaru', 'seat', 'fiat', 'dacia', 'chevrolet', 'jeep', 'mini', 'porsche', 'man', 'scania', 'iveco', 'renaulttrucks'];
const pixelsPerSecond = 18;

// Native scrolling also works with touch, trackpads and keyboard. The repeated
// groups let the carousel loop without duplicating its accessible content.
export default class BrandSlider extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.hovered = false;
    this.focused = false;
    this.drag = null;
    this.touching = false;
    this.inView = true;
    this.offset = 0;
    this.resumeAt = 0;
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.track = this.element.querySelector('.BsiBrandSlider-track');
    this.rail = this.track.querySelector('.BsiBrandSlider-rail');
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.motionChanged = () => {
      this.reducedMotion = this.motion.matches;
      if (this.reducedMotion) this.freeze();
    };
    this.motionChanged();
    this.motion.addEventListener('change', this.motionChanged);
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.track);
    this.measure();
    this.visibilityChanged = () => this.freeze();
    document.addEventListener('visibilitychange', this.visibilityChanged);
    this.intersectionObserver = new IntersectionObserver(_ref => {
      let [entry] = _ref;
      this.inView = entry.isIntersecting;
      if (!this.inView) this.freeze();
    });
    this.intersectionObserver.observe(this.track);
    this.tick = time => {
      const canPlay = !this.reducedMotion && !this.hovered && !this.focused && !this.drag && !this.touching && this.inView && document.visibilityState === 'visible' && time >= this.resumeAt;
      if (canPlay && this.cycleWidth) {
        if (!this.playing) {
          this.offset = (this.track.scrollLeft % this.cycleWidth + this.cycleWidth) % this.cycleWidth;
          this.playing = true;
          this.jump(this.cycleWidth);
        }
        const elapsed = this.lastFrame == null ? 0 : Math.min(time - this.lastFrame, 100);
        this.offset = (this.offset + pixelsPerSecond * elapsed / 1000) % this.cycleWidth;
        // A transform preserves subpixel movement even when native scrollLeft
        // rounds to whole pixels. The copies make the reset visually identical.
        this.rail.style.transform = `translate3d(${-this.offset}px,0,0)`;
        this.lastFrame = time;
      } else this.freeze();
      this.animationFrame = window.requestAnimationFrame(this.tick);
    };
    this.animationFrame = window.requestAnimationFrame(this.tick);
  }
  onremove(vnode) {
    window.cancelAnimationFrame(this.animationFrame);
    window.clearTimeout(this.scrollTimer);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.motion?.removeEventListener('change', this.motionChanged);
    document.removeEventListener('visibilitychange', this.visibilityChanged);
    super.onremove(vnode);
  }
  measure() {
    const groups = this.track.querySelectorAll('.BsiBrandSlider-group');
    if (groups.length < 2) return;
    const nextCycle = groups[1].offsetLeft - groups[0].offsetLeft;
    if (nextCycle === this.cycleWidth) return;
    this.freeze();
    const relative = this.cycleWidth ? this.track.scrollLeft % this.cycleWidth / this.cycleWidth : 0;
    this.cycleWidth = nextCycle;
    this.jump(nextCycle * (1 + relative));
  }
  freeze() {
    if (this.playing) {
      const left = this.track.scrollLeft + this.offset;
      this.rail.style.transform = '';
      this.jump(left);
      this.offset = 0;
      this.playing = false;
    }
    this.lastFrame = null;
  }
  jump(left) {
    // CSS smooth scrolling must not animate the equivalent position in a copy.
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = left;
    this.track.style.scrollBehavior = '';
  }
  settled() {
    if (this.playing) return;
    this.resumeAt = performance.now() + 500;
    window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => {
      if (this.drag || this.touching || !this.cycleWidth) return;
      const left = this.track.scrollLeft;
      if (left < this.cycleWidth - 1) this.jump(left + this.cycleWidth);else if (left >= this.cycleWidth * 2 - 1) this.jump(left - this.cycleWidth);
    }, 200);
  }
  move(direction) {
    this.freeze();
    const cards = this.track?.querySelectorAll('.BsiBrandSlider-card');
    if (!cards || cards.length < 2) return;
    const step = cards[1].offsetLeft - cards[0].offsetLeft;
    this.track.scrollBy({
      left: direction * step,
      behavior: this.reducedMotion ? 'auto' : 'smooth'
    });
  }
  pointerDown(event) {
    this.freeze();
    // Let the browser handle touch gestures and vertical page scrolling.
    if (event.pointerType === 'touch') {
      this.touching = true;
      return;
    }
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    this.drag = {
      id: event.pointerId,
      x: event.clientX,
      left: this.track.scrollLeft
    };
    this.track.classList.add('is-dragging');
    this.track.setPointerCapture(event.pointerId);
    event.preventDefault();
  }
  pointerMove(event) {
    if (!this.drag || event.pointerId !== this.drag.id) return;
    this.track.scrollLeft = this.drag.left - (event.clientX - this.drag.x);
  }
  pointerUp(event) {
    // Native scrolling cancels pointer events before the finger is lifted.
    // Touch end/cancel below keeps autoplay paused for the whole gesture.
    if (event.pointerType === 'touch') return;
    if (!this.drag || event.pointerId !== this.drag.id) return;
    this.drag = null;
    this.track.classList.remove('is-dragging');
    if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
    this.settled();
  }
  touchEnd(event) {
    this.touching = event.touches.length > 0;
    if (!this.touching) this.settled();
  }
  view() {
    const assets = (app.forum.attribute('assetsBaseUrl') || '').replace(/\/$/, '');
    return m("section", {
      className: "BsiBrandSlider",
      role: "region",
      "aria-roledescription": text('brands.carousel'),
      "aria-labelledby": "bsi-brands-title",
      onpointerenter: event => {
        event.redraw = false;
        if (event.pointerType === 'mouse') {
          this.hovered = true;
          this.freeze();
        }
      },
      onpointerleave: event => {
        event.redraw = false;
        this.hovered = false;
      },
      onfocusin: event => {
        event.redraw = false;
        this.focused = true;
        this.freeze();
      },
      onfocusout: event => {
        event.redraw = false;
        this.focused = !!event.currentTarget.contains(event.relatedTarget);
      }
    }, m("div", {
      className: "BsiBrandSlider-heading"
    }, m("div", null, m("span", {
      className: "BsiEyebrow"
    }, t('brands.eyebrow')), m("h2", {
      id: "bsi-brands-title"
    }, t('brands.title')))), m("div", {
      className: "BsiBrandSlider-window"
    }, m("div", {
      className: "BsiBrandSlider-track",
      tabindex: "0",
      "aria-label": text('brands.navigation'),
      "aria-describedby": "bsi-brands-hint",
      onscroll: event => {
        event.redraw = false;
        this.settled();
      },
      onpointerdown: event => {
        event.redraw = false;
        this.pointerDown(event);
      },
      onpointermove: event => {
        event.redraw = false;
        this.pointerMove(event);
      },
      onpointerup: event => {
        event.redraw = false;
        this.pointerUp(event);
      },
      onpointercancel: event => {
        event.redraw = false;
        this.pointerUp(event);
      },
      ontouchend: event => {
        event.redraw = false;
        this.touchEnd(event);
      },
      ontouchcancel: event => {
        event.redraw = false;
        this.touchEnd(event);
      },
      onwheel: event => {
        event.redraw = false;
        this.freeze();
        this.resumeAt = performance.now() + 700;
      },
      onkeydown: event => {
        event.redraw = false;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(event.key === 'ArrowRight' ? 1 : -1);
        }
      }
    }, m("div", {
      className: "BsiBrandSlider-rail"
    }, [0, 1, 2].map(group => m("div", {
      className: "BsiBrandSlider-group",
      key: group,
      role: group === 1 ? 'list' : undefined,
      "aria-hidden": group === 1 ? undefined : 'true'
    }, brands.map(brand => m("div", {
      className: 'BsiBrandSlider-card BsiBrandSlider-card--' + brand,
      role: group === 1 ? 'listitem' : undefined,
      key: brand
    }, m("span", {
      className: "BsiBrandSlider-emblem"
    }, m("img", {
      src: `${assets}/extensions/bsi-orders/brands/${brand}.svg?v=brands-3`,
      alt: text('brands.logo', {
        brand: text('brands.names.' + brand)
      }),
      width: "60",
      height: "60",
      draggable: "false"
    })), m("span", {
      className: "BsiBrandSlider-name"
    }, t('brands.names.' + brand))))))))), m("p", {
      className: "BsiBrandSlider-hint",
      id: "bsi-brands-hint"
    }, t('brands.hint')));
  }
}
flarum.reg.add('bsi-orders', 'forum/components/BrandSlider', BrandSlider);